import React, { useState, useEffect, useCallback } from 'react';
import { TableProps } from 'components';
import { HistoryController } from 'api';
import errorReporter from '@app/errorReporter';
import moment from 'moment';
import { capitalize } from 'utils';
import { ParsedUserNoPopulate } from '@bb/domain/dist/types';
import { TransferState, TransferType } from '@bb/users';
import { FindLog } from 'api/HistoryController/findLog';
import { FindTransferLogsByUserId } from 'api/HistoryController/findTransferLogsByUserId';
import { BorderedTable } from './StyledComponents';
import TableEntry from './TableEntry';
import { UserHistoryModalType } from './constants';

interface TableRecordType {
  status: string;
  user: string;
  date: string;
}

const columns: TableProps['columns'] = [
  {
    title: 'STATUS',
    dataIndex: 'status',
    key: 'status',
    width: 200,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'USER',
    dataIndex: 'user',
    key: 'user',
    width: 170,
    render: value => <TableEntry value={value} breakAll />,
  },
  {
    title: 'DATE',
    dataIndex: 'date',
    key: 'date',
    render: value => <TableEntry value={value} breakAll />,
  },
];

const getStatus = (
  state: TransferState,
  type: TransferType,
  isBrokerBot: boolean,
) => {
  const TypeMap: Record<TransferType, string> = {
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    new: 'New Agent',
  };
  switch (state) {
    case TransferState.Active:
      return 'Accepted';
    case TransferState.Denied:
    case TransferState.Cancelled:
      return capitalize(state);
    default:
      return `${capitalize(state)} ${isBrokerBot ? '' : TypeMap[type] || ''}`;
  }
};

interface TransferHistoryTableProps {
  transferId?: string;
  userId?: string;
  type: UserHistoryModalType;
}

// Filters only changes in transfer state and type given history diffs
// The initial history diff should always contain the initial state and type of the transfer doc.
// Subsequent diffs will only contain one or the other (depending on what has changed). However, when
// displaying to the user we need to display the type and the state, so we use can use the previous state/type
const buildActions = (
  transferHistoryData: FindLog | FindTransferLogsByUserId,
) => {
  const actions: (TableRecordType & { key: string })[] = [];

  const prevStateAndTypeRecord: Record<
    string,
    { prevState: TransferState; prevType: TransferType }
  > = {};

  // Fallback defaults, assumes manual transfer.
  const defaultState: TransferState = TransferState.Pending;
  const defaultType: TransferType = TransferType.Incoming;
  transferHistoryData
    .sort((log1, log2) =>
      // Sort from oldest to newest since the oldest logEntry will contain the initial transfer state and type
      new Date(log1.updatedAt) > new Date(log2.updatedAt) ? 1 : -1,
    )
    .forEach(logEntry => {
      let stateOrTypeChanged = false;
      logEntry.diffs.forEach(diff => {
        switch (diff.field) {
          case 'Transfer status': {
            prevStateAndTypeRecord[logEntry.objId] = {
              ...prevStateAndTypeRecord[logEntry.objId],
              prevState: diff.to,
            };
            stateOrTypeChanged = true;
            break;
          }
          case 'Transfer type': {
            prevStateAndTypeRecord[logEntry.objId] = {
              ...prevStateAndTypeRecord[logEntry.objId],
              prevType: diff.to,
            };
            stateOrTypeChanged = true;
            break;
          }
          default: {
            break;
          }
        }
      });

      if (!stateOrTypeChanged) return;

      if (
        !prevStateAndTypeRecord[logEntry.objId].prevState ||
        !prevStateAndTypeRecord[logEntry.objId].prevType
      ) {
        // The defaults should never be used, the prevState, prevType should always be defined from the first diff record
        errorReporter.error(
          `The transfer history for doc ${logEntry.objId} was not properly initialized with a state and type`,
        );
      }

      const user = logEntry.creationMethod
        ? { firstName: logEntry.creationMethod, lastName: '' }
        : (logEntry.user as ParsedUserNoPopulate);
      actions.push({
        key: `${logEntry.updatedAt}`,
        date: logEntry.updatedAt,
        status: getStatus(
          prevStateAndTypeRecord[logEntry.objId].prevState || defaultState,
          prevStateAndTypeRecord[logEntry.objId].prevType || defaultType,
          logEntry.creationMethod === 'BrokerBot',
        ),
        user: `${user?.firstName || ''} ${user?.lastName || ''}`,
      });
    });

  return actions;
};

const TransferHistoryTable = ({
  transferId,
  type,
  userId,
}: TransferHistoryTableProps) => {
  const [transferHistory, setTransferHistory] = useState<
    TableProps['dataSource']
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async () => {
    try {
      let historyData: FindLog | FindTransferLogsByUserId;
      if (type === UserHistoryModalType.TRANSFER) {
        historyData = await HistoryController.findLog({
          type: 'Transfer',
          id: transferId,
        });
      } else {
        const data = await HistoryController.findTransferLogsByUserId({
          userId,
        });
        historyData = data;
      }

      if (!historyData || historyData.length === 0) {
        setTransferHistory([]);
        setLoading(false);
      }
      const actions = buildActions(historyData);
      const sortedActions = actions
        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
        .map(action => ({
          ...action,
          date: moment(action.date).format('MMM D YYYY, h:mma'),
        }));

      setTransferHistory(sortedActions || []);
      setLoading(false);
    } catch (err) {
      errorReporter.error(err);
      setLoading(false);
    }
  }, [transferId, type, userId]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return (
    <BorderedTable
      dataSource={transferHistory}
      columns={columns}
      pagination={{
        pageSize: 5,
        showSizeChanger: false,
        position: ['bottomLeft'],
      }}
      loading={loading}
      type="primary"
      hideShadow
      scroll={{ x: '100%' }}
    />
  );
};

export default TransferHistoryTable;
